import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import VariantMatrix from '../../components/VariantMatrix';
import './VariantBuilderDrawer.css';

/**
 * Variant builder drawer.
 *
 * Right-slide drawer on desktop (≥768px), bottom sheet on mobile. Hosts
 * the existing <VariantMatrix> so the merchant can configure attributes
 * and variants in a focused surface instead of inside the main form.
 *
 * The drawer keeps its own draft state so the merchant can experiment
 * without committing — clicking "Done" copies the draft back to the
 * form via `onChange`. Closing without "Done" discards changes.
 *
 * Props:
 *   open         — whether the drawer is visible
 *   onClose      — called when the merchant closes without saving
 *   value        — current variantMatrix from the form
 *   onChange     — called with the new variantMatrix when merchant clicks Done
 *   basePrice    — used to seed default variant prices
 *   baseSku      — used to seed default variant SKUs
 *   attributeLimit — tier-based limit on attribute count
 *   disabled     — whether editing is allowed
 */
const VariantBuilderDrawer = ({
  open,
  onClose,
  value,
  onChange,
  basePrice = 0,
  baseSku = '',
  attributeLimit,
  disabled = false,
}) => {
  const [draft, setDraft] = useState(value || { attributes: [], variants: [] });
  const overlayRef = useRef(null);

  // Re-seed the draft each time the drawer opens so cancel = revert.
  useEffect(() => {
    if (open) setDraft(value || { attributes: [], variants: [] });
  }, [open, value]);

  // ESC to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const handleBackdrop = (e) => {
    if (e.target === overlayRef.current) onClose?.();
  };

  const handleDone = () => {
    onChange?.(draft);
    onClose?.();
  };

  const attrCount = draft?.attributes?.length || 0;
  const variantCount = draft?.variants?.length || 0;

  return (
    <div
      ref={overlayRef}
      className={`vbd-overlay ${open ? 'open' : ''}`}
      onMouseDown={handleBackdrop}
      aria-hidden={!open}
    >
      <aside
        className="vbd-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Configure variations"
      >
        <header className="vbd-header">
          <div className="vbd-header-text">
            <h2 className="vbd-title">Variations</h2>
            <p className="vbd-subtitle">
              {attrCount > 0
                ? `${attrCount} attribute${attrCount === 1 ? '' : 's'} · ${variantCount} variant${variantCount === 1 ? '' : 's'}`
                : 'Add attributes like Size or Color — each combination becomes a variant.'}
            </p>
          </div>
          <button
            type="button"
            className="vbd-close"
            onClick={onClose}
            aria-label="Close variations drawer"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="vbd-body">
          <VariantMatrix
            value={draft}
            onChange={setDraft}
            basePrice={basePrice}
            baseSku={baseSku}
            attributeLimit={attributeLimit}
            disabled={disabled}
          />
        </div>

        <footer className="vbd-footer">
          <button type="button" className="vbd-btn vbd-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="vbd-btn vbd-btn-primary"
            onClick={handleDone}
            disabled={disabled}
          >
            Done
          </button>
        </footer>
      </aside>
    </div>
  );
};

export default VariantBuilderDrawer;
